import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  Download,
  Upload,
  Palette,
  Send,
  Building,
  Calendar,
  RotateCcw,
  Factory,
  Edit2,
  Check,
  X,
  Plus,
  MapPin,
  PhoneCall,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Layers,
  ChevronRight,
} from "lucide-react";
import { api, uploadFile, downloadDocument } from "../../lib/api";
import { compactMoney, money, testid } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import { AssignDesignerDialog } from "./Design";
import MoveBackStageModal from "./MoveBackStageModal";
import OrderHistoryTrail from "./OrderHistoryTrail";

const DESIGN_STAGES = [
  "New",
  "In Progress",
  "Awaiting Approval",
  "Approved"
];

export default function DesignDetail({ orderId, onBack, onChange, user }) {
  const [order, setOrder] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [selectedProductOrderId, setSelectedProductOrderId] = useState(null);
  const [batchAssignModalOpen, setBatchAssignModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [designersList, setDesignersList] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);

  // Editing specs state
  const [editingSpecs, setEditingSpecs] = useState(false);
  const [specsText, setSpecsText] = useState("");

  // Collapsible section toggles
  const [historyOpen, setHistoryOpen] = useState(false);
  const [clientInfoOpen, setClientInfoOpen] = useState(true);

  // Modals / dialogs inside DesignDetail
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [movingBack, setMovingBack] = useState(false);
  const [movingBackBatch, setMovingBackBatch] = useState(false);
  const [selectedBatchOrderIds, setSelectedBatchOrderIds] = useState([]);
  const [bulkMoveModal, setBulkMoveModal] = useState({ open: false, mode: "all" });

  const [uploadModal, setUploadModal] = useState({
    open: false,
    mode: "add", // "add" | "replace"
    fileType: "artwork", // "artwork" | "reference"
    targetItem: null,
  });

  const canAct = isAdmin(user) || canAccessDepartment(user, ["Sales", "Design"]);
  const canApprove = isAdmin(user) || canAccessDepartment(user, ["Design", "Sales", "Management"]);

  // Load detailed order or batch workspace
  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const isExplicitBatchId = typeof orderId === "string" && orderId.toUpperCase().startsWith("BATCH-");

      if (isExplicitBatchId) {
        const batchRes = await api("get", `/designs/batch/${orderId}`);
        const bData = batchRes.data;
        setBatchData(bData);
        if (bData.orders && bData.orders.length > 0) {
          const activeOrder = (selectedProductOrderId && bData.orders.find((o) => o.order_id === selectedProductOrderId)) || bData.orders[0];
          setOrder(activeOrder);
          setSelectedProductOrderId(activeOrder.order_id);
          setSpecsText(activeOrder.design_specs || "");
        }
        return;
      }

      // Single order request
      const res = await api("get", `/orders/${orderId}`);
      const data = res.data;

      // Extract design specs if not directly separated
      if (!data.design_specs && data.notes && data.notes.includes("--- Design Specifications ---")) {
        const parts = data.notes.split("--- Design Specifications ---");
        data.design_specs = parts[1].trim();
        data.general_notes = parts[0].trim();
      }

      // If this order is part of a bulk batch, load the batch context too
      if (data.upload_batch_id) {
        try {
          const batchRes = await api("get", `/designs/batch/${data.upload_batch_id}`);
          if (batchRes.data && batchRes.data.orders && batchRes.data.orders.length > 1) {
            setBatchData(batchRes.data);
            setSelectedProductOrderId(data.order_id);
          } else {
            setBatchData(null);
          }
        } catch (e) {
          setBatchData(null);
        }
      } else {
        setBatchData(null);
      }

      setOrder(data);
      setSpecsText(data.design_specs || "");
    } catch (err) {
      console.error("Failed to load design item:", err);
      toast.error("Could not load design details");
    } finally {
      setLoading(false);
    }
  }, [orderId, selectedProductOrderId]);

  useEffect(() => {
    loadOrder();
    api("get", "/users?role=design")
      .then((r) => setDesignersList(r.data))
      .catch(() => { });
    api("get", "/vendors?status=Active")
      .then((r) => {
        if (r.data && r.data.length > 0) {
          setVendorsList(r.data.map((v) => ({ email: v.vendor_id, name: v.name, ...v })));
        } else {
          api("get", "/users?role=production")
            .then((uRes) => setVendorsList(uRes.data || []))
            .catch(() => {});
        }
      })
      .catch(() => {
        api("get", "/users?role=production")
          .then((uRes) => setVendorsList(uRes.data || []))
          .catch(() => {});
      });
  }, [loadOrder]);

  // Switch active product inside batch
  const handleSelectProduct = (productOrder) => {
    setSelectedProductOrderId(productOrder.order_id);
    setOrder(productOrder);
    setSpecsText(productOrder.design_specs || "");
  };

  const toggleSelectBatchOrder = (oid) => {
    setSelectedBatchOrderIds((prev) =>
      prev.includes(oid) ? prev.filter((id) => id !== oid) : [...prev, oid]
    );
  };

  const handleSelectAllBatchOrders = () => {
    if (!batchData?.orders) return;
    if (selectedBatchOrderIds.length === batchData.orders.length) {
      setSelectedBatchOrderIds([]);
    } else {
      setSelectedBatchOrderIds(batchData.orders.map((o) => o.order_id));
    }
  };

  // Batch assign designer to all products in batch
  const handleBatchAssignDesigner = async (designerName) => {
    if (!batchData) return;
    setBusyAction("batch_assign");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/design-stage`, {
        design_stage: "In Progress",
        assigned_designer: designerName,
        remarks: `Designer ${designerName} assigned to entire batch`,
      });
      toast.success(`Assigned ${designerName} to all products in batch`);
      setBatchAssignModalOpen(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to assign designer to batch");
    } finally {
      setBusyAction("");
    }
  };

  // Batch start all new products in batch
  const handleBatchStartAll = async () => {
    if (!batchData) return;
    setBusyAction("batch_start");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/design-stage`, {
        design_stage: "In Progress",
        remarks: "Bulk start: products moved to In Progress",
      });
      toast.success("All products in batch moved to In Progress");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to update batch stages");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Submit for Approval for all products in batch
  const handleBatchSubmitApproval = async (notes = "") => {
    if (!batchData) return;
    setBusyAction("batch_submit");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/design-stage`, {
        design_stage: "Awaiting Approval",
        version_comments: notes || "Artwork submitted for bulk batch review",
      });
      toast.success(`Bulk Request #${batchData.batch_id} submitted for approval (All products)`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not submit batch for approval";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Approve or Request Revision for all products in batch
  const handleBatchApprovalDecision = async (decision, comments = "") => {
    if (!batchData) return;
    setBusyAction("batch_approval");
    try {
      const isYes = decision === "Yes";
      await api("post", `/orders/batch/${batchData.batch_id}/approvals`, {
        status: isYes ? "Approved" : "Not Approved",
        comments: comments || (isYes ? "Bulk Design Approved" : "Bulk Design Revisions Requested"),
        approver: user?.name || "Authorized Approver",
      });
      if (isYes) {
        toast.success(`Bulk Request #${batchData.batch_id} approved (All products)`);
      } else {
        toast.success(`Bulk Request #${batchData.batch_id} returned for revision (In Progress)`);
      }
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not record batch approval";
      toast.error(typeof msg === "string" ? msg : "Batch approval failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Request Production Details from Production team
  const handleBatchRequestProduction = async (remarks = "") => {
    if (!batchData) return;
    setBusyAction("batch_request_prod");
    try {
      await api("post", `/orders/batch/${batchData.batch_id}/request-production`, {
        remarks: remarks || "Production details requested for bulk batch",
      });
      toast.success(`Production details requested for all products in Bulk Request #${batchData.batch_id}`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not request production details";
      toast.error(typeof msg === "string" ? msg : "Request failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Send to Production
  const handleBatchSendToProduction = async () => {
    if (!batchData) return;
    setBusyAction("batch_send_prod");
    try {
      await api("post", `/orders/batch/${batchData.batch_id}/send-to-production`);
      toast.success(`Bulk Request #${batchData.batch_id} sent to Production queue (Not Started)`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not send bulk request to production";
      toast.error(typeof msg === "string" ? msg : "Send failed");
      loadOrder();
    } finally {
      setBusyAction("");
    }
  };

  // Stage update helper for current product
  const handleUpdateStage = async (targetStage, extraData = {}) => {
    const oid = order?.order_id || orderId;
    setBusyAction(targetStage);
    try {
      await api("patch", `/orders/${oid}/design-stage`, {
        design_stage: targetStage,
        ...extraData,
      });
      toast.success(`Stage updated to ${targetStage}`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update stage";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyAction("");
    }
  };

  // Unified file upload / update handler for current product
  const handleUploadUnifiedFile = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const file = formData.get("file");
    const fileType = formData.get("file_type") || uploadModal.fileType;
    const comments = formData.get("comments") || "";

    if (!file || file.size === 0) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploadModal((prev) => ({ ...prev, open: false }));
    setBusyAction("upload_unified_file");

    const oid = order?.order_id || orderId;

    try {
      if (fileType === "artwork") {
        await uploadFile(oid, file, "Design File");
        await api("post", `/orders/${oid}/designs`, {
          comments: comments || (uploadModal.mode === "replace" ? `Updated version replacing ${uploadModal.targetItem?.name || "previous"}` : `Artwork proof`),
          designer: order?.assigned_designer || user?.name || "Designer",
          file_name: file.name,
        });
        toast.success(`Artwork proof "${file.name}" uploaded successfully`);
      } else {
        await uploadFile(oid, file, "Design Reference");
        toast.success(`Reference file "${file.name}" uploaded successfully`);
      }
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload file");
    } finally {
      setBusyAction("");
    }
  };

  // Save updated design specifications for current product
  const handleSaveSpecs = async () => {
    const oid = order?.order_id || orderId;
    setBusyAction("save_specs");
    try {
      const currentNotes = order?.general_notes || order?.notes || "";
      const cleanGeneral = currentNotes.split("--- Design Specifications ---")[0].trim();
      const newCombinedNotes = specsText.trim()
        ? (cleanGeneral ? `${cleanGeneral}\n\n--- Design Specifications ---\n${specsText.trim()}` : `--- Design Specifications ---\n${specsText.trim()}`)
        : cleanGeneral;

      await api("patch", `/orders/${oid}`, {
        notes: newCombinedNotes,
      });
      toast.success("Design specifications updated");
      setEditingSpecs(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to update specifications");
    } finally {
      setBusyAction("");
    }
  };

  // Simple Yes / No Approval Decision handler
  const handleApprovalDecision = async (decision) => {
    const oid = order?.order_id || orderId;
    setBusyAction("approval");
    try {
      const isYes = decision === "Yes";
      await api("post", `/orders/${oid}/approvals`, {
        status: isYes ? "Approved" : "Not Approved",
        approver: user?.name || "Authorized User",
      });
      if (isYes) {
        toast.success(`Design for order ${oid} marked Approved`);
      } else {
        toast.success(`Design for order ${oid} marked Not Approved (Returned for revision)`);
      }
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not record design approval";
      toast.error(typeof msg === "string" ? msg : "Approval failed");
    } finally {
      setBusyAction("");
    }
  };

  // Request Production Vendor & Warehouse
  const handleRequestProduction = async () => {
    const oid = order?.order_id || orderId;
    setBusyAction("request_prod");
    try {
      await api("post", `/orders/${oid}/request-production`);
      toast.success("Production details requested from Production team");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not request production details";
      toast.error(typeof msg === "string" ? msg : "Request failed");
    } finally {
      setBusyAction("");
    }
  };

  // Send to Production after details are confirmed
  const handleSendToProduction = async () => {
    const oid = order?.order_id || orderId;
    setBusyAction("send_prod");
    setOrder((prev) =>
      prev
        ? {
            ...prev,
            current_stage: "Production",
            design_stage: "Sent to Production",
            production_status: "Not Started",
          }
        : prev
    );
    try {
      await api("post", `/orders/${oid}/send-to-production`);
      toast.success("Order sent to Production queue (Not Started)");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not send to production";
      toast.error(typeof msg === "string" ? msg : "Send failed");
      loadOrder();
    } finally {
      setBusyAction("");
    }
  };

  const versions = order?.designs || [];
  const maxVersion = versions.reduce((max, v) => Math.max(max, v.version || 1), 0);

  // Unified file list combining reference assets and versioned artwork proofs
  const unifiedFileList = useMemo(() => {
    if (!order) return [];
    const list = [];
    const orderDocs = order.documents || [];
    const orderDesigns = order.designs || [];
    const currentMaxVersion = orderDesigns.reduce((max, v) => Math.max(max, v.version || 1), 0);

    // 1. Artwork proofs from order.designs (sorted descending by version: V2, V1)
    const sortedDesigns = [...orderDesigns].sort((a, b) => (b.version || 0) - (a.version || 0));
    sortedDesigns.forEach((v) => {
      const matchDoc = orderDocs.find((d) =>
        (v.file_id && d.document_id === v.file_id) ||
        (v.file_name && d.name === v.file_name) ||
        (d.category === "Design File" && d.name?.includes(`V${v.version}`))
      ) || orderDocs.find((d) => d.category === "Design File");

      list.push({
        id: v.design_id || `ds_${v.version}`,
        name: v.file_name || `Artwork Proof V${v.version}`,
        category: "Artwork Proof",
        isArtwork: true,
        version: v.version,
        isLatest: v.version === currentMaxVersion,
        uploaded_by: v.designer || order.assigned_designer || "Designer",
        created_at: v.created_at,
        comments: v.comments || "",
        status: v.status || (v.version === currentMaxVersion ? "Current" : "Previous"),
        downloadUrl: matchDoc ? downloadDocument(matchDoc.document_id) : (v.file_id ? downloadDocument(v.file_id) : null),
        docId: matchDoc?.document_id || v.file_id,
      });
    });

    // 2. Reference & other documents
    orderDocs.forEach((d) => {
      const isAlreadyArtwork = list.some((item) => item.docId === d.document_id || item.name === d.name);
      if (!isAlreadyArtwork) {
        list.push({
          id: d.document_id,
          name: d.name,
          category: d.category || "Design Reference",
          isArtwork: false,
          version: null,
          isLatest: false,
          uploaded_by: d.uploaded_by || "User",
          created_at: d.created_at,
          comments: "",
          status: "Active",
          downloadUrl: downloadDocument(d.document_id),
          docId: d.document_id,
        });
      }
    });

    return list;
  }, [order]);

  if (loading && !order) {
    return (
      <div className="content">
        <div className="loading-screen">Loading design workspace…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="content">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Design Pipeline
        </button>
        <div className="panel" style={{ padding: 32, textAlign: "center", marginTop: 16 }}>
          <h3 style={{ margin: 0, color: "var(--navy)" }}>Design item not found</h3>
          <p className="muted" style={{ marginTop: 4 }}>This order might have been moved or deleted.</p>
        </div>
      </div>
    );
  }

  const isSentToProduction =
    order.design_stage === "Sent to Production" ||
    [
      "Production",
      "Dispatch",
      "Challan",
      "Delivery",
      "Delivered",
      "Accounts",
      "Invoice",
      "Payment",
      "Completed",
    ].includes(order.current_stage);

  const currentStage = isSentToProduction
    ? "Sent to Production"
    : order.design_stage || "New";

  const currentStageIndex = isSentToProduction
    ? DESIGN_STAGES.length
    : DESIGN_STAGES.indexOf(currentStage);

  const batchPrimaryStage = batchData?.primaryStage || batchData?.primary_stage || (
    batchData?.counts?.Approved === batchData?.orders?.length && batchData?.orders?.length > 0 ? "Approved" :
    batchData?.counts?.["Awaiting Approval"] === batchData?.orders?.length && batchData?.orders?.length > 0 ? "Awaiting Approval" :
    batchData?.counts?.["In Progress"] > 0 ? "In Progress" :
    batchData?.counts?.["Awaiting Approval"] > 0 ? "Awaiting Approval" :
    "New"
  );

  return (
    <div className="content design-detail-content" data-testid="design-detail-view" style={{ maxWidth: 1280, margin: "0 auto", paddingBottom: 40 }}>
      {/* Top Bar: Back button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button
          className="back-btn"
          data-testid="design-detail-back-button"
          onClick={onBack}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}
        >
          <ArrowLeft size={14} /> Back to Design Pipeline
        </button>
      </div>

      {/* Batch Overview Ribbon (Compact & Clean when viewing a bulk batch) */}
      {batchData && (
        <div
          className="panel"
          style={{
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 14,
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
          data-testid="batch-overview-banner"
        >
          {/* Batch Title & Actions Strip */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0284c7",
                  background: "#e0f2fe",
                  padding: "2px 7px",
                  borderRadius: 4,
                  fontFamily: "var(--mono)",
                }}
              >
                #{batchData.batch_id}
              </span>
              <strong style={{ fontSize: 14, color: "var(--navy)" }}>
                {batchData.client?.company_name || order.client_name}
              </strong>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                · {batchData.orders?.length || 0} Products · Total: <b style={{ color: "var(--navy)" }}>{money(batchData.total_value)}</b>
              </span>
              <span
                className={`badge ${
                  batchPrimaryStage === "Approved" ? "green" :
                  batchPrimaryStage === "Awaiting Approval" ? "amber" :
                  batchPrimaryStage === "In Progress" ? "purple" : "blue"
                }`}
                style={{ fontSize: 10.5, padding: "2px 7px" }}
              >
                Batch: {batchPrimaryStage}
              </span>
            </div>

            {/* Batch Level Quick Actions */}
            {canAct && (
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {/* Move Selected Back (visible when products are checked) */}
                {selectedBatchOrderIds.length > 0 && (
                  <button
                    type="button"
                    className="outline-btn compact"
                    style={{
                      color: "#b45309",
                      borderColor: "#fde68a",
                      background: "#fffbeb",
                      height: 28,
                      fontSize: 11.5,
                      padding: "0 9px",
                      fontWeight: 600,
                    }}
                    onClick={() => setBulkMoveModal({ open: true, mode: "selected" })}
                    data-testid="batch-move-selected-back-btn"
                    title="Move selected products back to their previous workflow stage"
                  >
                    <RotateCcw size={12} /> Move Selected Back ({selectedBatchOrderIds.length})
                  </button>
                )}

                {/* Move All Back (always available at bulk request level near Assign All and Start All) */}
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{
                    color: "#b45309",
                    borderColor: "#fde68a",
                    background: "#fffbeb",
                    height: 28,
                    fontSize: 11.5,
                    padding: "0 9px",
                    fontWeight: 600,
                  }}
                  onClick={() => setBulkMoveModal({ open: true, mode: "all" })}
                  data-testid="batch-move-all-back-btn"
                  title="Move all eligible products in this bulk request back to their previous workflow stage"
                >
                  <RotateCcw size={12} /> Move All Back
                </button>

                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => setBatchAssignModalOpen(true)}
                  data-testid="batch-assign-designer-btn"
                  style={{ height: 28, fontSize: 11.5, padding: "0 9px" }}
                >
                  <User size={12} /> Assign All
                </button>

                {batchPrimaryStage === "New" && batchData.counts?.New > 0 && (
                  <button
                    type="button"
                    className="primary-btn compact"
                    onClick={handleBatchStartAll}
                    disabled={busyAction === "batch_start"}
                    data-testid="batch-start-all-btn"
                    style={{ height: 28, fontSize: 11.5, padding: "0 10px", background: "#0284c7", borderColor: "#0284c7" }}
                  >
                    <ArrowRight size={12} /> Start All ({batchData.counts.New})
                  </button>
                )}

                {batchPrimaryStage === "In Progress" && (
                  <button
                    type="button"
                    className="primary-btn compact"
                    onClick={() => handleBatchSubmitApproval()}
                    disabled={busyAction === "batch_submit"}
                    data-testid="batch-submit-approval-btn"
                    style={{ height: 28, fontSize: 11.5, padding: "0 10px", background: "#7c3aed", borderColor: "#7c3aed" }}
                  >
                    <Send size={12} /> Submit All ({batchData.orders.length})
                  </button>
                )}

                {batchPrimaryStage === "Awaiting Approval" && (
                  <>
                    <button
                      type="button"
                      className="outline-btn compact"
                      style={{ color: "#dc2626", borderColor: "#fca5a5", height: 28, fontSize: 11.5, padding: "0 9px" }}
                      onClick={() => handleBatchApprovalDecision("No")}
                      disabled={Boolean(busyAction)}
                      data-testid="batch-revision-btn"
                    >
                      <RotateCcw size={12} /> Revision (All)
                    </button>
                    <button
                      type="button"
                      className="primary-btn compact"
                      onClick={() => handleBatchApprovalDecision("Yes")}
                      disabled={Boolean(busyAction)}
                      data-testid="batch-approve-btn"
                      style={{ height: 28, fontSize: 11.5, padding: "0 10px", background: "#16a34a", borderColor: "#16a34a" }}
                    >
                      <CheckCircle2 size={12} /> Approve All
                    </button>
                  </>
                )}

                {batchPrimaryStage === "Approved" && (() => {
                  const hasVendor = Boolean(batchData.production_vendor_name);
                  const hasWarehouse = Boolean(batchData.warehouse_name);
                  const isBatchConfirmed = batchData.production_request_status === "confirmed" && (hasVendor || hasWarehouse);
                  const isBatchRequested = !isBatchConfirmed && (
                    batchData.production_request_status === "requested" ||
                    batchData.production_request_status === "pending" ||
                    batchData.orders?.some((o) => o.production_request_status === "requested" || o.production_request_status === "pending")
                  );

                  return (
                    <>
                      {isBatchConfirmed ? (
                        <button
                          type="button"
                          className="primary-btn compact"
                          onClick={handleBatchSendToProduction}
                          disabled={Boolean(busyAction)}
                          data-testid="batch-send-prod-btn"
                          style={{ height: 28, fontSize: 11.5, padding: "0 10px", background: "#4f46e5", borderColor: "#4f46e5" }}
                        >
                          <Send size={12} /> Send Batch to Production
                        </button>
                      ) : isBatchRequested ? (
                        <span
                          style={{
                            background: "#fffbeb",
                            border: "1px solid #fde68a",
                            borderRadius: 4,
                            padding: "4px 8px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11.5,
                            color: "#92400e",
                            fontWeight: 600,
                          }}
                        >
                          <Clock size={12} color="#d97706" /> Prod Details Requested
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="primary-btn compact"
                          onClick={() => handleBatchRequestProduction()}
                          disabled={Boolean(busyAction)}
                          data-testid="batch-request-prod-btn"
                          style={{ height: 28, fontSize: 11.5, padding: "0 10px", background: "#0284c7", borderColor: "#0284c7" }}
                        >
                          <Factory size={12} /> Request Production (All)
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Product Switcher Tabs & Bulk Selection Checkboxes */}
          {batchData.orders && batchData.orders.length > 1 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }} data-testid="batch-product-tabs">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "#64748b" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none", fontWeight: 600, color: "var(--navy)" }}>
                    <input
                      type="checkbox"
                      checked={batchData.orders.length > 0 && selectedBatchOrderIds.length === batchData.orders.length}
                      onChange={handleSelectAllBatchOrders}
                      data-testid="batch-select-all-checkbox"
                      style={{ cursor: "pointer", accentColor: "#0284c7" }}
                    />
                    <span>Select All Products ({batchData.orders.length})</span>
                  </label>
                  {selectedBatchOrderIds.length > 0 && (
                    <span style={{ color: "#0284c7", fontWeight: 600 }}>
                      · {selectedBatchOrderIds.length} selected
                    </span>
                  )}
                </div>
                {selectedBatchOrderIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedBatchOrderIds([])}
                    style={{ background: "none", border: "none", color: "#64748b", fontSize: 11, cursor: "pointer", textDecoration: "underline", padding: 0 }}
                  >
                    Clear Selection
                  </button>
                )}
              </div>

              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
                {batchData.orders.map((p) => {
                  const isSelected = p.order_id === order.order_id;
                  const isChecked = selectedBatchOrderIds.includes(p.order_id);
                  const pStage = p.design_stage || "New";
                  const isApproved = pStage === "Approved";
                  const isInProgress = pStage === "In Progress";
                  const isAwaiting = pStage === "Awaiting Approval";

                  return (
                    <div
                      key={p.order_id}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 6,
                        border: isSelected ? "1.5px solid #0284c7" : isChecked ? "1.5px solid #93c5fd" : "1px solid #e2e8f0",
                        background: isSelected ? "#f0f9ff" : isChecked ? "#f8fafc" : "#ffffff",
                        boxShadow: isSelected ? "0 1px 3px rgba(2, 132, 199, 0.1)" : "none",
                        padding: "2px 8px 2px 8px",
                        gap: 6,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectBatchOrder(p.order_id);
                        }}
                        data-testid={`batch-product-checkbox-${p.order_id}`}
                        style={{ cursor: "pointer", accentColor: "#0284c7" }}
                        title="Select product for bulk action"
                      />
                      <button
                        type="button"
                        onClick={() => handleSelectProduct(p)}
                        data-testid={`batch-product-tab-${p.order_id}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "4px 2px",
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          fontWeight: isSelected ? 600 : 500,
                          color: isSelected ? "#0369a1" : "#334155",
                        }}
                      >
                        <span>{p.product}</span>
                        <span style={{ fontSize: 11, color: isSelected ? "#0284c7" : "#64748b" }}>
                          ({p.quantity} {p.unit || "pcs"})
                        </span>
                        <span
                          className={`badge ${
                            isApproved ? "green" : isAwaiting ? "amber" : isInProgress ? "purple" : "blue"
                          }`}
                          style={{ fontSize: 9, padding: "1px 5px", marginLeft: 2 }}
                        >
                          {pStage}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Product Header Card */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 14,
          boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
        }}
        data-testid="design-product-header-card"
      >
        {/* Top Header Row: Order ID, Client Name, and Right Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>
              #{order.order_id}
            </span>
            <span style={{ color: "#cbd5e1" }}>•</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
              {order.client_name}
            </span>
          </div>

          {/* Right Header Controls: Move Back & Badges */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {order.current_stage !== "Order Received" && (
              <button
                type="button"
                className="outline-btn compact"
                data-testid="design-header-move-back-btn"
                style={{
                  color: "#b45309",
                  borderColor: "#fde68a",
                  background: "#fffbeb",
                  fontWeight: 600,
                  fontSize: 12,
                  height: 30,
                }}
                onClick={() => setMovingBack(true)}
                title="Move order back to previous stage"
              >
                <RotateCcw size={12} /> Move Back
              </button>
            )}

            <span
              className={`badge ${
                isSentToProduction || currentStage === "Approved"
                  ? "green"
                  : currentStage === "Awaiting Approval"
                  ? "amber"
                  : currentStage === "In Progress"
                  ? "purple"
                  : "blue"
              }`}
              style={{ fontSize: 12, padding: "4px 10px", fontWeight: 600 }}
              data-testid="current-design-stage-badge"
            >
              Stage: {currentStage}
            </span>

            <span
              className={`badge ${order.priority === "Urgent" ? "red" : order.priority === "High" ? "amber" : "blue"}`}
              style={{ fontSize: 12, padding: "4px 10px" }}
            >
              {order.priority || "Normal"}
            </span>
          </div>
        </div>

        {/* Product Title Prominently */}
        <h1
          style={{
            margin: "0 0 4px",
            fontSize: 22,
            color: "var(--navy)",
            fontWeight: 800,
            lineHeight: 1.25,
          }}
          data-testid="design-product-title"
        >
          {order.product}
        </h1>

        {/* Order Description Directly Below Product Name - Bold, Prominent, Clean, Non-Boxed */}
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: order.description?.trim() ? "#0f172a" : "#94a3b8",
            fontStyle: order.description?.trim() ? "normal" : "italic",
            lineHeight: 1.5,
            margin: "4px 0 12px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
          data-testid="design-order-description-text"
        >
          {order.description?.trim() ? order.description.trim() : "No description provided"}
        </div>

        {/* Product Meta Details: Quantity, Value, Due Date */}
        <div style={{ fontSize: 12.5, color: "#64748b", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span>Quantity: <strong style={{ color: "var(--navy)" }}>{(order.quantity || 0).toLocaleString()} {order.unit || "units"}</strong></span>
          <span>•</span>
          <span>Value: <strong style={{ color: "var(--navy)" }}>{money(order.total_value)}</strong></span>
          <span>•</span>
          <span>Due Date: <strong style={{ color: "var(--navy)" }}>{order.required_delivery_date || "—"}</strong></span>
        </div>
      </div>

      {/* Modern Compact Design Lifecycle Stepper */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: "10px 16px",
          marginBottom: 16,
        }}
        data-testid="design-lifecycle-timeline"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${DESIGN_STAGES.length}, 1fr)`,
            gap: 8,
            alignItems: "center",
          }}
        >
          {DESIGN_STAGES.map((s, i) => {
            const isDone = i < currentStageIndex;
            const isCurrent = i === currentStageIndex;

            return (
              <div
                key={s}
                data-testid={`design-step-${testid(s)}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: isCurrent ? "#eff6ff" : isDone ? "#f0fdf4" : "#f8fafc",
                  border: isCurrent ? "1.5px solid #3b82f6" : isDone ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                  transition: "all 0.15s ease",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    background: isCurrent ? "#2563eb" : isDone ? "#16a34a" : "#cbd5e1",
                    color: "#ffffff",
                    flexShrink: 0,
                  }}
                >
                  {isDone ? "✓" : i + 1}
                </div>
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: isCurrent ? 700 : isDone ? 600 : 500,
                      color: isCurrent ? "#1d4ed8" : isDone ? "#15803d" : "#64748b",
                    }}
                  >
                    {s}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2-Column Responsive Workspace Grid */}
      <div className="dashboard-grid" style={{ gridTemplateColumns: "1.9fr 1.1fr", gap: 16, alignItems: "flex-start" }}>

        {/* =======================================================
            LEFT COLUMN: DESIGN ASSETS, SPECS, APPROVAL & HISTORY
           ======================================================= */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>

          {/* Panel: Design Assets & Specifications */}
          <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 }} data-testid="design-assets-specs-panel">
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Palette size={16} color="#0284c7" />
                <h3 style={{ margin: 0, fontSize: 15, color: "var(--navy)", fontWeight: 700 }}>
                  Design Assets &amp; Specifications
                </h3>
                <span style={{ fontSize: 11, color: "#64748b", background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>
                  {unifiedFileList.length} {unifiedFileList.length === 1 ? "file" : "files"}
                </span>
              </div>
              {canAct && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {!editingSpecs && (
                    <button
                      type="button"
                      className="outline-btn compact"
                      onClick={() => setEditingSpecs(true)}
                      data-testid="edit-specs-button"
                      style={{ height: 28, fontSize: 11.5, padding: "0 8px" }}
                    >
                      <Edit2 size={12} /> Edit Specs
                    </button>
                  )}
                  <button
                    type="button"
                    className="primary-btn compact"
                    onClick={() =>
                      setUploadModal({
                        open: true,
                        mode: "add",
                        fileType: "artwork",
                        targetItem: null,
                      })
                    }
                    data-testid="add-file-button"
                    style={{ height: 28, fontSize: 11.5, padding: "0 10px" }}
                  >
                    <Plus size={12} /> Add File
                  </button>
                </div>
              )}
            </div>

            {/* Design Specifications Text */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
                Specifications &amp; Guidelines
              </div>
              {editingSpecs ? (
                <div>
                  <textarea
                    rows={3}
                    value={specsText}
                    onChange={(e) => setSpecsText(e.target.value)}
                    placeholder="e.g. Dimensions: 8.5x11, CMYK 300 DPI, corporate blue (#0369a1), matte finish..."
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "1.5px solid var(--blue)",
                      fontSize: 12.5,
                      lineHeight: 1.5,
                      fontFamily: "inherit",
                    }}
                    data-testid="edit-specs-textarea"
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 6 }}>
                    <button
                      type="button"
                      className="outline-btn compact"
                      style={{ height: 26, fontSize: 11.5 }}
                      onClick={() => {
                        setEditingSpecs(false);
                        setSpecsText(order.design_specs || "");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="primary-btn compact"
                      style={{ height: 26, fontSize: 11.5 }}
                      onClick={handleSaveSpecs}
                      disabled={busyAction === "save_specs"}
                      data-testid="save-specs-button"
                    >
                      <Check size={12} /> Save Specs
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    background: order.design_specs ? "#f8fafc" : "#fafafa",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: "8px 12px",
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: order.design_specs ? "#1e293b" : "#64748b",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {order.design_specs ? (
                    order.design_specs
                  ) : (
                    <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                      No specifications added yet. Click &quot;Edit Specs&quot; to add dimensions, color guidelines, and requirements.
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Files & Artwork Proofs Table */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
                Artwork Proofs &amp; Reference Files
              </div>

              {unifiedFileList.length === 0 ? (
                <div
                  style={{
                    padding: "16px",
                    border: "1px dashed #cbd5e1",
                    borderRadius: 6,
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: 12,
                    background: "#f8fafc",
                  }}
                >
                  No files or artwork uploaded yet. Click &quot;Add File&quot; to upload reference assets or artwork proofs.
                </div>
              ) : (
                <div className="table-wrap" style={{ border: "1px solid #e2e8f0", borderRadius: 6 }}>
                  <table>
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={{ padding: "8px 10px", fontSize: 11 }}>File Name</th>
                        <th style={{ padding: "8px 10px", fontSize: 11 }}>Type</th>
                        <th style={{ padding: "8px 10px", fontSize: 11 }}>Version</th>
                        <th style={{ padding: "8px 10px", fontSize: 11 }}>Uploaded By</th>
                        <th style={{ padding: "8px 10px", fontSize: 11 }}>Date</th>
                        <th style={{ padding: "8px 10px", fontSize: 11 }}>Status</th>
                        <th style={{ padding: "8px 10px", fontSize: 11, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unifiedFileList.map((item) => (
                        <tr key={item.id} data-testid={`file-row-${item.id}`}>
                          {/* File Name & Notes */}
                          <td style={{ padding: "8px 10px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <FileText size={15} color={item.isArtwork ? "#7c3aed" : "#0284c7"} style={{ flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <b style={{ color: "var(--navy)", fontSize: 12, display: "block" }}>{item.name}</b>
                                {item.comments && (
                                  <small style={{ color: "#64748b", fontSize: 10.5, display: "block" }}>
                                    {item.comments}
                                  </small>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Type */}
                          <td style={{ padding: "8px 10px" }}>
                            <span
                              className={`badge ${item.isArtwork ? "purple" : "blue"}`}
                              style={{ fontSize: 10, padding: "1px 5px" }}
                            >
                              {item.category}
                            </span>
                          </td>

                          {/* Version */}
                          <td style={{ padding: "8px 10px" }}>
                            {item.version ? (
                              <span
                                className={`badge ${item.isLatest ? "green" : "amber"}`}
                                style={{ fontSize: 10, padding: "1px 5px" }}
                              >
                                V{item.version}
                              </span>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>
                            )}
                          </td>

                          {/* Uploaded By */}
                          <td style={{ padding: "8px 10px", fontSize: 11.5, color: "#334155" }}>
                            {item.uploaded_by}
                          </td>

                          {/* Upload Date */}
                          <td style={{ padding: "8px 10px", fontSize: 11, color: "#64748b" }}>
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                          </td>

                          {/* Status */}
                          <td style={{ padding: "8px 10px" }}>
                            {item.isArtwork ? (
                              item.isLatest ? (
                                <span className="badge green" style={{ fontSize: 10, padding: "1px 5px" }}>
                                  Current
                                </span>
                              ) : (
                                <span className="badge" style={{ background: "#f1f5f9", color: "#64748b", fontSize: 10, padding: "1px 5px" }}>
                                  V{item.version}
                                </span>
                              )
                            ) : (
                              <span className="badge blue" style={{ fontSize: 10, padding: "1px 5px" }}>
                                Active
                              </span>
                            )}
                          </td>

                          {/* Actions: Download and Replace / Update */}
                          <td style={{ padding: "8px 10px", textAlign: "right" }}>
                            <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                              {item.downloadUrl ? (
                                <a
                                  href={item.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="outline-btn compact"
                                  style={{ padding: "2px 6px", fontSize: 11, textDecoration: "none", height: 24 }}
                                  title="Download file"
                                >
                                  <Download size={10} /> Get
                                </a>
                              ) : null}

                              {canAct && (
                                <button
                                  type="button"
                                  className="outline-btn compact"
                                  style={{ padding: "2px 6px", fontSize: 11, height: 24 }}
                                  onClick={() =>
                                    setUploadModal({
                                      open: true,
                                      mode: "replace",
                                      fileType: item.isArtwork ? "artwork" : "reference",
                                      targetItem: item,
                                    })
                                  }
                                  title={item.isArtwork ? "Upload new artwork version" : "Update file"}
                                >
                                  <RefreshCw size={10} /> {item.isArtwork ? "New Ver" : "Update"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* Design Approval Summary Card */}
          {(() => {
            const approvalStatus = order.approval_status || (order.design_stage === "Approved" ? "Approved" : "Pending");
            const isApproved = approvalStatus === "Approved" || order.design_stage === "Approved";
            const isNotApproved = approvalStatus === "Not Approved" || approvalStatus === "Rejected";
            const latestApproval = (order.approvals && order.approvals.length > 0) ? order.approvals[0] : null;

            return (
              <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }} data-testid="design-approval-panel">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircle2 size={16} color={isApproved ? "#16a34a" : isNotApproved ? "#dc2626" : "#d97706"} />
                    <h3 style={{ margin: 0, color: "var(--navy)", fontSize: 14, fontWeight: 700 }}>Design Approval</h3>
                  </div>
                  <span className={`badge ${isApproved ? "green" : isNotApproved ? "red" : "amber"}`} style={{ fontSize: 11, padding: "2px 8px" }}>
                    {isApproved ? "Approved" : isNotApproved ? "Revision Needed" : "Pending Approval"}
                  </span>
                </div>

                {isApproved ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#334155", display: "flex", gap: 16, flexWrap: "wrap", background: "#f0fdf4", padding: "8px 12px", borderRadius: 6, border: "1px solid #bbf7d0" }}>
                    <div>Approved by: <b>{order.approved_by || latestApproval?.approver || "Authorized Approver"}</b></div>
                    <div>Approved on: <b>{order.approved_at ? new Date(order.approved_at).toLocaleString() : (latestApproval?.created_at ? new Date(latestApproval.created_at).toLocaleString() : "—")}</b></div>
                  </div>
                ) : isNotApproved ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#991b1b", background: "#fef2f2", padding: "8px 12px", borderRadius: 6, border: "1px solid #fecaca" }}>
                    <div>Returned for revision by: <b>{order.rejected_by || order.approved_by || latestApproval?.approver || "Authorized User"}</b></div>
                    {latestApproval?.comments && (
                      <div style={{ marginTop: 4, fontSize: 11.5, color: "#7f1d1d" }}>
                        <b>Notes:</b> {latestApproval.comments}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                    Artwork is awaiting sign-off. Use the action console to record the approval decision.
                  </div>
                )}
              </section>
            );
          })()}

          {/* Design Activity Log / History Trail (Collapsible) */}
          <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }} data-testid="design-history-trail-panel">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setHistoryOpen(!historyOpen)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Clock size={15} color="#0284c7" />
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>Design History Trail</h3>
                <span style={{ fontSize: 11, color: "#64748b", background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>
                  {order.history?.length || 0} events
                </span>
              </div>
              <button
                type="button"
                className="icon-btn"
                style={{ padding: 4, color: "#64748b" }}
                aria-label={historyOpen ? "Collapse history" : "Expand history"}
              >
                {historyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {historyOpen && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
                <OrderHistoryTrail history={order.history || []} />
              </div>
            )}
          </section>

        </div>

        {/* =======================================================
            RIGHT COLUMN: STAGE ACTION CONSOLE & CLIENT CONTEXT
           ======================================================= */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>

          {/* Action Console Box */}
          <section
            className="panel"
            style={{
              border: "1.5px solid #bfdbfe",
              background: "#ffffff",
              borderRadius: 8,
              padding: 16,
              boxShadow: "0 1px 3px rgba(37, 99, 235, 0.06)",
            }}
            data-testid="design-stage-action-console"
          >
            <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#0284c7", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Current Stage Action
              </div>
              <h3 style={{ margin: "2px 0 0", color: "var(--navy)", fontSize: 16, fontWeight: 700 }}>
                {currentStage}
              </h3>
            </div>

            {/* Batch Context Pill if in Batch */}
            {batchData && (
              <div
                style={{
                  marginBottom: 10,
                  padding: "5px 8px",
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: 4,
                  fontSize: 11,
                  color: "#0369a1",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span><strong>Batch #{batchData.batch_id}</strong></span>
                <span>{batchData.orders?.length || 0} Products</span>
              </div>
            )}

            {/* STAGE: SENT TO PRODUCTION */}
            {isSentToProduction && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#15803d", fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
                    <CheckCircle2 size={16} color="#16a34a" /> Handed off to Production
                  </div>
                  <p style={{ margin: 0, fontSize: 11.5, color: "#166534", lineHeight: 1.4 }}>
                    Active in the Production department workflow.
                  </p>
                </div>

                <div style={{ color: "#334155", fontSize: 12, lineHeight: 1.5, background: "#f8fafc", padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                  <div><b>Vendor:</b> {order.production_vendor_name || order.assigned_producer || "—"}</div>
                  <div><b>Warehouse:</b> {order.warehouse_name || "—"}</div>
                  <div style={{ marginTop: 2 }}>
                    <b>Status:</b>{" "}
                    <span className="badge blue" style={{ fontSize: 10.5, padding: "1px 5px" }}>
                      {order.production_status || "Not Started"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* STAGE: NEW */}
            {currentStage === "New" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.45 }}>
                  Assign a designer to start work on this order.
                </p>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: 11.5, color: "#64748b" }}>Designer:</span>
                  <button
                    className="designer-pill"
                    onClick={() => setShowAssignModal(true)}
                    data-testid="console-assign-btn"
                    style={{ fontSize: 12 }}
                  >
                    <span className="designer-avatar">
                      {(order.assigned_designer || "D").charAt(0).toUpperCase()}
                    </span>
                    <span>{order.assigned_designer || "Assign Designer"}</span>
                  </button>
                </div>

                {canAct && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                    {batchData && batchData.counts?.New > 0 && (
                      <button
                        className="primary-btn wide"
                        style={{ background: "#0284c7", borderColor: "#0284c7", justifyContent: "center", height: 32, fontSize: 12 }}
                        onClick={handleBatchStartAll}
                        disabled={Boolean(busyAction)}
                        data-testid="console-batch-start-button"
                      >
                        Start All ({batchData.counts.New} Products) <ArrowRight size={13} />
                      </button>
                    )}
                    <button
                      className={batchData ? "outline-btn wide" : "primary-btn wide"}
                      style={batchData ? { fontSize: 12, justifyContent: "center", height: 32 } : { background: "#0284c7", borderColor: "#0284c7", height: 32, fontSize: 12 }}
                      onClick={() => handleUpdateStage("In Progress")}
                      disabled={Boolean(busyAction)}
                      data-testid="console-start-design-button"
                    >
                      {batchData ? `Start This Product Only` : "Start Design"} <ArrowRight size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* STAGE: IN PROGRESS */}
            {currentStage === "In Progress" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.45 }}>
                  Artwork is being prepared. Submit for sign-off once ready.
                </p>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: 11.5, color: "#64748b" }}>Designer:</span>
                  <button
                    className="designer-pill"
                    onClick={() => setShowAssignModal(true)}
                    data-testid="console-reassign-btn"
                    style={{ fontSize: 12 }}
                  >
                    <span className="designer-avatar">
                      {(order.assigned_designer || "D").charAt(0).toUpperCase()}
                    </span>
                    <span>{order.assigned_designer || "Reassign"}</span>
                  </button>
                </div>

                {canAct && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                    {batchData && (
                      <button
                        className="primary-btn wide"
                        style={{ background: "#7c3aed", borderColor: "#7c3aed", justifyContent: "center", height: 32, fontSize: 12 }}
                        onClick={() => handleBatchSubmitApproval()}
                        disabled={Boolean(busyAction)}
                        data-testid="console-batch-submit-approval-button"
                      >
                        Submit All ({batchData.orders.length}) <ArrowRight size={13} />
                      </button>
                    )}
                    <button
                      className={batchData ? "outline-btn wide" : "primary-btn wide"}
                      style={batchData ? { fontSize: 12, justifyContent: "center", height: 32 } : { background: "#7c3aed", borderColor: "#7c3aed", height: 32, fontSize: 12 }}
                      onClick={() => handleUpdateStage("Awaiting Approval")}
                      disabled={Boolean(busyAction)}
                      data-testid="console-submit-approval-button"
                    >
                      {batchData ? `Submit This Product Only` : "Submit for Approval"} <ArrowRight size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* STAGE: AWAITING APPROVAL */}
            {currentStage === "Awaiting Approval" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.45 }}>
                  Artwork submitted for review. Record approval below:
                </p>

                {canApprove && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {batchData && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="primary-btn"
                          style={{ flex: 1, background: "#16a34a", borderColor: "#16a34a", justifyContent: "center", height: 32, fontSize: 11.5 }}
                          onClick={() => handleBatchApprovalDecision("Yes")}
                          disabled={Boolean(busyAction)}
                          data-testid="console-approve-batch-yes-btn"
                        >
                          <CheckCircle2 size={13} /> Approve All ({batchData.orders.length})
                        </button>
                        <button
                          className="outline-btn"
                          style={{ flex: 1, color: "#dc2626", borderColor: "#fca5a5", justifyContent: "center", height: 32, fontSize: 11.5 }}
                          onClick={() => handleBatchApprovalDecision("No")}
                          disabled={Boolean(busyAction)}
                          data-testid="console-approve-batch-no-btn"
                        >
                          <RotateCcw size={12} /> Revision (All)
                        </button>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, ...(batchData ? { borderTop: "1px dashed #e2e8f0", paddingTop: 6 } : {}) }}>
                      <button
                        className={batchData ? "outline-btn" : "primary-btn"}
                        style={batchData ? { flex: 1, fontSize: 11.5, color: "#16a34a", borderColor: "#86efac", justifyContent: "center", height: 32 } : { flex: 1, background: "#16a34a", borderColor: "#16a34a", justifyContent: "center", height: 32, fontSize: 12 }}
                        onClick={() => handleApprovalDecision("Yes")}
                        disabled={Boolean(busyAction)}
                        data-testid="console-approve-yes-btn"
                      >
                        <CheckCircle2 size={13} /> {batchData ? "Approve This" : "Approve Design"}
                      </button>
                      <button
                        className="outline-btn"
                        style={{ flex: 1, fontSize: 11.5, color: "#dc2626", borderColor: "#fca5a5", justifyContent: "center", height: 32 }}
                        onClick={() => handleApprovalDecision("No")}
                        disabled={Boolean(busyAction)}
                        data-testid="console-approve-no-btn"
                      >
                        <RotateCcw size={12} /> {batchData ? "Revision This" : "Request Revision"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STAGE: APPROVED */}
            {currentStage === "Approved" && (() => {
              const reqStatus = order.production_request_status;
              const hasVendor = Boolean(order.production_vendor_name || order.assigned_producer);
              const hasWarehouse = Boolean(order.warehouse_name);
              const isConfirmed = reqStatus === "confirmed" && (hasVendor || hasWarehouse);
              const isRequested = !isConfirmed && (reqStatus === "requested" || reqStatus === "pending");

              if (isConfirmed) {
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "8px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#166534", fontWeight: 700, fontSize: 12, marginBottom: 4 }}>
                        <CheckCircle2 size={14} color="#16a34a" /> Production Details Confirmed
                      </div>
                      <div style={{ color: "#334155", fontSize: 11.5, lineHeight: 1.45 }}>
                        <div><b>Vendor:</b> {order.production_vendor_name || order.assigned_producer || "—"}</div>
                        <div><b>Warehouse:</b> {order.warehouse_name || "—"}</div>
                      </div>
                    </div>

                    {canAct && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 2 }}>
                        {batchData ? (
                          <>
                            <button
                              className="primary-btn wide"
                              style={{ background: "#4f46e5", borderColor: "#4f46e5", justifyContent: "center", height: 32, fontSize: 11.5 }}
                              onClick={handleBatchSendToProduction}
                              disabled={Boolean(busyAction)}
                              data-testid="console-batch-send-production-button"
                            >
                              <Send size={13} /> Send Entire Batch ({batchData.orders.length})
                            </button>
                            <button
                              className="outline-btn wide"
                              style={{ fontSize: 11.5, justifyContent: "center", height: 32 }}
                              onClick={handleSendToProduction}
                              disabled={Boolean(busyAction)}
                              data-testid="console-send-production-button"
                            >
                              <Send size={12} /> Send This Product Only
                            </button>
                          </>
                        ) : (
                          <button
                            className="primary-btn wide"
                            style={{ background: "#4f46e5", borderColor: "#4f46e5", height: 32, fontSize: 12 }}
                            onClick={handleSendToProduction}
                            disabled={Boolean(busyAction)}
                            data-testid="console-send-production-button"
                          >
                            <Send size={13} /> Send to Production
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (isRequested) {
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#b45309", fontWeight: 700, fontSize: 12, marginBottom: 2 }}>
                        <Clock size={13} color="#d97706" /> Prod Details Requested
                      </div>
                      <p style={{ margin: 0, fontSize: 11.5, color: "#78350f", lineHeight: 1.4 }}>
                        Awaiting Production team to assign vendor &amp; warehouse.
                      </p>
                    </div>
                  </div>
                );
              }

              // Initial: Not requested yet
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.45 }}>
                    Design is approved. Request vendor &amp; warehouse assignment:
                  </p>

                  {canAct && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {batchData ? (
                        <>
                          <button
                            className="primary-btn wide"
                            style={{ background: "#0284c7", borderColor: "#0284c7", justifyContent: "center", height: 32, fontSize: 11.5 }}
                            onClick={() => handleBatchRequestProduction()}
                            disabled={Boolean(busyAction)}
                            data-testid="console-batch-request-production-button"
                          >
                            <Factory size={13} /> Request Production (All)
                          </button>
                          <button
                            className="outline-btn wide"
                            style={{ fontSize: 11.5, justifyContent: "center", height: 32 }}
                            onClick={handleRequestProduction}
                            disabled={Boolean(busyAction)}
                            data-testid="console-request-production-button"
                          >
                            <Factory size={12} /> Request This Product
                          </button>
                        </>
                      ) : (
                        <button
                          className="primary-btn wide"
                          style={{ background: "#0284c7", borderColor: "#0284c7", height: 32, fontSize: 12 }}
                          onClick={handleRequestProduction}
                          disabled={Boolean(busyAction)}
                          data-testid="console-request-production-button"
                        >
                          <Factory size={13} /> Request for Production
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </section>

          {/* Client Information Card (Compact & Collapsible) */}
          {(() => {
            const client = order.client_details || {};
            const companyName = client.company_name || order.client_name || "—";
            const contactPerson = client.contact_person || order.client_contact_person || "—";
            const phone = canViewClientPhone(user) ? (client.phone || order.client_phone || "") : "";
            const email = client.email || order.client_email || "";
            const address = client.shipping_address || client.billing_address || "";
            const paymentTerms = client.payment_terms || "Net 30";
            const clientStatus = client.status || "Active";
            const mailSubject = encodeURIComponent(`Design Clarification: Order ${order.order_id} - ${order.product || ""}`);

            return (
              <section
                className="panel"
                data-testid="design-client-info-panel"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => setClientInfoOpen(!clientInfoOpen)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Building size={15} color="#0284c7" />
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>Client Information</h3>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="badge green" style={{ fontSize: 10, padding: "1px 6px" }}>
                      {clientStatus}
                    </span>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ padding: 2, color: "#64748b" }}
                      aria-label={clientInfoOpen ? "Collapse client info" : "Expand client info"}
                    >
                      {clientInfoOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                  </div>
                </div>

                {clientInfoOpen && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
                    {/* Quick Call / Email Buttons */}
                    {(phone || email) && (
                      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                        {phone && (
                          <a
                            href={`tel:${phone}`}
                            className="primary-btn compact"
                            style={{
                              flex: 1,
                              justifyContent: "center",
                              background: "#059669",
                              borderColor: "#059669",
                              textDecoration: "none",
                              fontSize: 11.5,
                              gap: 4,
                              height: 28,
                            }}
                            data-testid="client-call-btn"
                            title={`Call ${phone}`}
                          >
                            <PhoneCall size={12} /> Call
                          </a>
                        )}
                        {email && (
                          <a
                            href={`mailto:${email}?subject=${mailSubject}`}
                            className="outline-btn compact"
                            style={{
                              flex: 1,
                              justifyContent: "center",
                              color: "#0284c7",
                              borderColor: "#0284c7",
                              textDecoration: "none",
                              fontSize: 11.5,
                              gap: 4,
                              height: 28,
                            }}
                            data-testid="client-email-btn"
                            title={`Email ${email}`}
                          >
                            <Mail size={12} /> Email
                          </a>
                        )}
                      </div>
                    )}

                    {/* Key Details */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
                      <div>
                        <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Company</span>
                        <b style={{ color: "var(--navy)" }} data-testid="client-company-name">{companyName}</b>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div>
                          <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Contact</span>
                          <span data-testid="client-contact-person">{contactPerson}</span>
                        </div>
                        {phone && canViewClientPhone(user) && (
                          <div style={{ textAlign: "right" }}>
                            <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Phone</span>
                            <a href={`tel:${phone}`} style={{ color: "#0284c7", fontWeight: 600, textDecoration: "none" }} data-testid="client-phone-link">
                              {phone}
                            </a>
                          </div>
                        )}
                      </div>

                      {email && (
                        <div>
                          <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Email</span>
                          <a href={`mailto:${email}?subject=${mailSubject}`} style={{ color: "#0284c7", textDecoration: "none", wordBreak: "break-all" }} data-testid="client-email-link">
                            {email}
                          </a>
                        </div>
                      )}

                      {address && (
                        <div>
                          <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Address</span>
                          <span style={{ color: "#334155" }} data-testid="client-billing-address client-shipping-address">{address}</span>
                        </div>
                      )}

                      {paymentTerms && (
                        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 6, marginTop: 2 }}>
                          <span style={{ color: "#64748b", fontSize: 11 }}>Payment Terms:</span>
                          <span style={{ color: "var(--navy)", fontWeight: 600, fontSize: 11 }}>{paymentTerms}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            );
          })()}

          {/* Order Specs Summary Card */}
          <section
            className="panel"
            data-testid="design-order-context-panel"
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: 14,
            }}
          >
            <h3 style={{ margin: "0 0 8px", fontSize: 13.5, fontWeight: 700, color: "var(--navy)" }}>
              Order Specs Summary
            </h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", fontSize: 12 }}>
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5 }}>Order ID</span>
                <b className="mono" style={{ color: "var(--navy)" }}>{order.order_id}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5 }}>Product</span>
                <b style={{ color: "var(--navy)" }}>{order.product}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5 }}>Quantity</span>
                <span>{(order.quantity || 0).toLocaleString()} units</span>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5 }}>Due Date</span>
                <b style={{ color: "#0284c7" }}>{order.required_delivery_date || "—"}</b>
              </div>
            </div>
          </section>

        </div>

      </div>

      {/* =======================================================
          MODALS & DIALOGS
         ======================================================= */}

      {/* Assign Designer Dialog (Single Product) */}
      {showAssignModal && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <form
            className="modal"
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const designer = formData.get("designer");
              setShowAssignModal(false);
              if (currentStage === "New") {
                handleUpdateStage("In Progress", { assigned_designer: designer });
              } else {
                handleUpdateStage(currentStage, { assigned_designer: designer });
              }
            }}
            style={{ maxWidth: 420 }}
            data-testid="design-detail-assign-modal"
          >
            <div className="modal-head">
              <div>
                <div className="small-label">ASSIGNMENT</div>
                <h2>Assign Designer</h2>
                <p className="muted">Order #{order.order_id} · {order.product}</p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setShowAssignModal(false)}>
                <X size={18} />
              </button>
            </div>

            <label className="full" style={{ marginTop: 16 }}>
              Select Designer
              <select
                name="designer"
                defaultValue={designersList.some((d) => d.name === order.assigned_designer) ? order.assigned_designer : ""}
                required
              >
                <option value="">-- Choose Designer --</option>
                {designersList.map((d) => (
                  <option key={d.user_id || d.email} value={d.name}>
                    {d.name} ({d.email})
                  </option>
                ))}
              </select>
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
              <button type="button" className="outline-btn" onClick={() => setShowAssignModal(false)}>
                Cancel
              </button>
              <button type="submit" className="primary-btn">
                Assign & Save
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Unified File Upload & Replace Modal */}
      {uploadModal.open && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10000 }}
        >
          <form
            className="modal"
            onSubmit={handleUploadUnifiedFile}
            style={{ maxWidth: 480 }}
            data-testid="unified-file-upload-modal"
          >
            <div className="modal-head">
              <div>
                <div className="small-label">
                  {uploadModal.mode === "replace" ? "UPDATE / REPLACE FILE" : "ADD DESIGN ASSET OR ARTWORK"}
                </div>
                <h2>{uploadModal.mode === "replace" ? "Update / Replace File" : "Upload File"}</h2>
                <p className="muted">Order #{order.order_id} · {order.product}</p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setUploadModal({ open: false, mode: "add", fileType: "artwork", targetItem: null })}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              {/* File Type Selection */}
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>
                  File Category / Purpose *
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "9px 12px",
                      borderRadius: 6,
                      border: `1px solid ${uploadModal.fileType === "artwork" ? "#7c3aed" : "#cbd5e1"}`,
                      background: uploadModal.fileType === "artwork" ? "#f5f3ff" : "#ffffff",
                      cursor: "pointer",
                      fontSize: 12.5,
                      fontWeight: uploadModal.fileType === "artwork" ? 600 : 400,
                      color: uploadModal.fileType === "artwork" ? "#6d28d9" : "#334155",
                    }}
                  >
                    <input
                      type="radio"
                      name="file_type"
                      value="artwork"
                      checked={uploadModal.fileType === "artwork"}
                      onChange={() => setUploadModal((prev) => ({ ...prev, fileType: "artwork" }))}
                      style={{ margin: 0 }}
                    />
                    Artwork Proof
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "9px 12px",
                      borderRadius: 6,
                      border: `1px solid ${uploadModal.fileType === "reference" ? "#0284c7" : "#cbd5e1"}`,
                      background: uploadModal.fileType === "reference" ? "#f0f9ff" : "#ffffff",
                      cursor: "pointer",
                      fontSize: 12.5,
                      fontWeight: uploadModal.fileType === "reference" ? 600 : 400,
                      color: uploadModal.fileType === "reference" ? "#0369a1" : "#334155",
                    }}
                  >
                    <input
                      type="radio"
                      name="file_type"
                      value="reference"
                      checked={uploadModal.fileType === "reference"}
                      onChange={() => setUploadModal((prev) => ({ ...prev, fileType: "reference" }))}
                      style={{ margin: 0 }}
                    />
                    Reference / Asset
                  </label>
                </div>
              </div>

              {/* Notice for artwork versions */}
              {uploadModal.fileType === "artwork" && (
                <div
                  style={{
                    background: "#fdf4ff",
                    border: "1px solid #f0abfc",
                    borderRadius: 6,
                    padding: "8px 12px",
                    fontSize: 12,
                    color: "#86198f",
                  }}
                >
                  This will register a new artwork iteration (V{maxVersion + 1}) and preserve all prior versions in the history.
                </div>
              )}

              {/* File input */}
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>
                  Select File (Image / PDF / Vector) *
                </label>
                <input
                  type="file"
                  name="file"
                  required
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    background: "#fff",
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    fontSize: 12.5,
                  }}
                />
              </div>

              {/* Comments / Version Notes */}
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>
                  {uploadModal.fileType === "artwork" ? "Version Changes / Summary" : "Asset Notes (Optional)"}
                </label>
                <textarea
                  name="comments"
                  rows={3}
                  placeholder={
                    uploadModal.fileType === "artwork"
                      ? "e.g., Color adjustments, bleed lines verified, updated client logo"
                      : "e.g., Client brand book, vector logo pack, high-res sample"
                  }
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    fontSize: 12.5,
                  }}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                padding: "14px 24px",
                borderTop: "1px solid var(--line)",
                background: "#f8fafc",
              }}
            >
              <button
                type="button"
                className="outline-btn"
                onClick={() => setUploadModal({ open: false, mode: "add", fileType: "artwork", targetItem: null })}
              >
                Cancel
              </button>
              <button type="submit" className="primary-btn">
                <Upload size={13} /> {uploadModal.fileType === "artwork" ? `Save Version (V${maxVersion + 1})` : "Upload File"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Vendor Selection Modal */}
      {showVendorModal && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            zIndex: 10000,
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const vendorId = formData.get("vendor_id");
              if (!vendorId) {
                toast.error("Please select a production vendor");
                return;
              }
              const vendor = vendorsList.find((v) => v.email === vendorId || v.vendor_id === vendorId);
              setShowVendorModal(false);
              handleUpdateStage("Sent to Production", {
                vendor_id: vendorId,
                vendor_name: vendor?.name || vendorId,
              });
            }}
            data-testid="design-detail-vendor-modal"
            style={{
              background: "#ffffff",
              width: "min(520px, 95vw)",
              borderRadius: "16px",
              boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(226, 232, 240, 0.8)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "linear-gradient(to right, #fafafa, #ffffff)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
                    color: "#4f46e5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(79, 70, 229, 0.12)",
                    flexShrink: 0,
                  }}
                >
                  <Factory size={22} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.8px",
                      color: "#4f46e5",
                      textTransform: "uppercase",
                      marginBottom: "2px",
                    }}
                  >
                    Send to Production
                  </div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#0f172a",
                      lineHeight: 1.2,
                    }}
                  >
                    Select Production Vendor
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowVendorModal(false)}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "13px",
                }}
              >
                <span style={{ fontWeight: 700, color: "#1e293b" }}>
                  Order #{order.order_id}
                </span>
                <span style={{ color: "#475569", fontWeight: 600 }}>
                  {order.client_name}
                </span>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#1e293b",
                    marginBottom: "8px",
                  }}
                >
                  Production Vendor / Shop Floor Team *
                </label>

                <select
                  name="vendor_id"
                  required
                  data-testid="vendor-select-dropdown"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "13.5px",
                    borderRadius: "8px",
                    border: "1.5px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#0f172a",
                  }}
                >
                  <option value="">-- Select Production Vendor --</option>
                  {vendorsList.map((v) => (
                    <option key={v.email || v.vendor_id} value={v.email || v.vendor_id}>
                      {v.name} {v.email ? `(${v.email})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "16px 24px",
                background: "#f8fafc",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <button
                type="button"
                className="outline-btn"
                onClick={() => setShowVendorModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                disabled={vendorsList.length === 0}
                data-testid="confirm-vendor-button"
                style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
              >
                <Send size={14} /> Send to Production
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Batch Assign Designer Modal */}
      {batchAssignModalOpen && batchData && (
        <AssignDesignerDialog
          order={{
            order_id: batchData.batch_id,
            product: `All ${batchData.orders.length} Products (${batchData.client.company_name})`,
            client_name: batchData.client.company_name,
          }}
          designers={designersList}
          user={user}
          onClose={() => setBatchAssignModalOpen(false)}
          onConfirm={(designerName) => handleBatchAssignDesigner(designerName)}
        />
      )}

      {/* Move Back Stage Modal (Single Product) */}
      {movingBack && order && (
        <MoveBackStageModal
          order={order}
          isOpen={movingBack}
          onClose={() => setMovingBack(false)}
          onSuccess={() => {
            loadOrder();
            onChange && onChange();
          }}
          user={user}
        />
      )}

      {/* Move Back Stage Modal (Bulk Request: All or Selected Products) */}
      {(bulkMoveModal.open || movingBackBatch) && batchData && (
        <MoveBackStageModal
          order={{
            isBatch: true,
            batch_id: batchData.batch_id,
            mode: bulkMoveModal.open ? bulkMoveModal.mode : "all",
            selected_order_ids: bulkMoveModal.open && bulkMoveModal.mode === "selected" ? selectedBatchOrderIds : null,
            product: `Bulk Request (${batchData.orders?.length || 0} Products)`,
            client_name: batchData.client?.company_name || order.client_name,
            orders: batchData.orders,
            counts: batchData.counts,
          }}
          isOpen={bulkMoveModal.open || movingBackBatch}
          onClose={() => {
            setBulkMoveModal({ open: false, mode: "all" });
            setMovingBackBatch(false);
          }}
          onSuccess={async (data) => {
            setSelectedBatchOrderIds([]);
            await loadOrder();
            onChange && onChange();
          }}
          user={user}
        />
      )}

    </div>
  );
}
